// <s-infinite-list>: appends the next page of [data-items] when [data-next] scrolls near view (or is clicked),
// and keeps going until the last page. Without JS, [data-next] is a normal link to the next page.
class SInfiniteList extends HTMLElement {
  #io;
  #loading = false;

  connectedCallback() {
    this.next = this.querySelector('[data-next]');
    if (!this.next) return;
    this.next.addEventListener('click', (event) => {
      event.preventDefault();
      this.#load();
    });
    this.#io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#load();
    }, { rootMargin: '0px 0px 600px 0px' });
    this.#io.observe(this.next);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }

  // Re-check after each load: an IntersectionObserver only fires on change, so if the button is still in view
  // (or a load was skipped while another was running) observing again triggers a fresh check.
  #recheck() {
    if (!this.next || !this.#io) return;
    this.#io.unobserve(this.next);
    this.#io.observe(this.next);
  }

  // Category pages render every matching recipe in ranked order; reveal the next nine hidden ones.
  #revealLocal() {
    const later = [...this.querySelectorAll('[data-items] > [data-later][hidden]')];
    later.slice(0, 9).forEach((item) => item.removeAttribute('hidden'));
    const count = this.querySelector('[data-count]');
    const total = this.querySelectorAll('[data-items] > *').length;
    const shown = total - Math.max(later.length - 9, 0);
    if (count?.dataset.countTemplate) count.textContent = count.dataset.countTemplate.replace('__SHOWN__', shown);
    if (later.length <= 9) {
      this.#io?.disconnect();
      this.next.closest('[data-next-wrap]')?.remove();
      this.next = null;
    } else {
      requestAnimationFrame(() => this.#recheck());
    }
  }

  async #load(attempt = 0) {
    if (this.next?.hasAttribute('data-local')) return this.#revealLocal();
    const url = this.next?.getAttribute('href');
    if (this.#loading || !url) return;
    this.#loading = true;
    this.toggleAttribute('data-loading', true);
    let ok = false;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newItems = doc.querySelectorAll('s-infinite-list [data-items] > *');
      // A busy/error page has no recipe list; that is not the end of the recipes, so keep the button and retry.
      if (!doc.querySelector('s-infinite-list') || newItems.length === 0) throw new Error('No recipes in response');
      const items = this.querySelector('[data-items]');
      newItems.forEach((item) => items.append(document.adoptNode(item)));
      const count = this.querySelector('[data-count]');
      const newCount = doc.querySelector('s-infinite-list [data-count]');
      if (count && newCount) count.textContent = newCount.textContent;
      const newNext = doc.querySelector('s-infinite-list [data-next]');
      if (newNext) {
        this.next.setAttribute('href', newNext.getAttribute('href'));
      } else {
        this.#io?.disconnect();
        this.next.closest('[data-next-wrap]')?.remove();
        this.next = null;
      }
      ok = true;
    } catch (error) {
      // Leave the link in place (it still works as a click or a normal link) and retry a few times, waiting longer each time.
    } finally {
      this.#loading = false;
      this.removeAttribute('data-loading');
    }
    if (ok) {
      requestAnimationFrame(() => this.#recheck());
    } else if (attempt < 3) {
      setTimeout(() => this.#load(attempt + 1), 1500 * (attempt + 1));
    }
  }
}

if (!customElements.get('s-infinite-list')) customElements.define('s-infinite-list', SInfiniteList);

// Recipe filter dropdowns (<details data-recipe-menu>): close when clicking outside or pressing Escape.
document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-recipe-menu][open]').forEach((menu) => {
    if (!menu.contains(event.target)) menu.removeAttribute('open');
  });
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('details[data-recipe-menu][open]').forEach((menu) => {
    menu.removeAttribute('open');
    menu.querySelector('summary')?.focus();
  });
});
